import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

import {
  adentroAhora as traerAdentro,
  autorizacion as traerAutorizacion,
  autorizados as traerAutorizados,
  autorizadosNoVigentes as traerNoVigentes,
  entradasRecientes as traerRecientes,
  estadoAutorizacion,
  TOPE_NO_VIGENTES,
  movimientos as traerMovimientos,
  registrarSalida,
} from "../services/vigilancia";
import {
  avisoDeError,
  filtrarPorPatenteONombre,
  haceCuanto,
  recientesSinAutorizar,
} from "../shared/autosAutorizados";
import { horaAr } from "../shared/fechaAr";
import Loading from "./Loading";
import Autorizados from "../components/vigilancia/Autorizados";
import FormEntrada from "../components/vigilancia/FormEntrada";
import Patente from "../components/funcionales/Patente";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import { buscarFila, useItemPedido } from "../hooks/useItemPedido";
import "./css/vigilancia.css";

/* Vigilancia — control de acceso al portón.
 *
 * ── LO QUE PIDIÓ NICO, EN DOS PARTES ───────────────────────────────────────
 *
 * 1. Deja AUTORIZADO de antemano a alguien ajeno a la empresa —un proveedor, un
 *    posible cliente, una visita— por nombre y/o patente, con la razón por la
 *    que entra. Vigilancia no lo deja pasar «porque sí»: la justificación es lo
 *    que habilita el ingreso. Eso es la whitelist de arriba.
 * 2. Vigilancia REGISTRA a todos los que entran y salen. La salida es la que
 *    contesta «¿quién está adentro ahora?» —la pregunta caliente del portón—,
 *    así que ese bloque va primero.
 *
 * ── EN EL PORTÓN SE VE LA PATENTE, NO EL NOMBRE ────────────────────────────
 *
 * Por eso al tipear la patente en el registro, la pantalla resuelve sola el
 * nombre (de lo que Nico autorizó, o de la nómina si es un camión de la flota)
 * y avisa si venía autorizado. Es el dolor concreto que Nico contó: «yo sólo
 * veo la patente».
 *
 * ── LOS AUTOS DEL PERSONAL (Guillermo, Portería, tickets 389/394 — 13/10) ──
 *
 * La gente de la casa entra todos los días con el mismo auto. Sus patentes
 * quedan en la lista con nombre y sector, y al escribir la patente o el nombre
 * en «Registrar entrada» aparece la persona: un toque y queda lista para
 * registrar, sin DNI ni «a quién viene». Esos autos los maneja la barrera
 * (sumar, corregir, dar de baja), porque la autorización ya la tiene la
 * portería antes de que el auto llegue.
 *
 * ── UN SOLO LISTADO (Nico, ticket 441 — 19/10) ─────────────────────────────
 *
 * «¿No puede haber un solo listado y un solo lugar para agregar gente?». Los
 * autos de la casa, las autorizaciones de afuera y las vencidas o dadas de
 * baja van en una sola lista, «Autorizados», con un solo «Agregar»; cada
 * renglón dice si es fijo, temporal, vencido o dado de baja
 * (`components/vigilancia/Autorizados.jsx`).
 *
 * ── DE MIRAR Y DE CARGAR, SEGÚN EL PERMISO ─────────────────────────────────
 *
 * `ver.vigilancia` abre la pantalla; `cargar.vigilancia` habilita registrar
 * entradas/salidas y manejar los autos del personal; `autorizar.vigilancia`,
 * además, las autorizaciones de gente de afuera (alta, baja, editar, hasta
 * cuándo vale). El candado real lo pone la RLS: sin la llave la base rebota la
 * escritura, así que los botones que no se pueden usar no se muestran, y no es
 * que «no anden».
 */

const TZ = "America/Argentina/Buenos_Aires";

const hora = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString("es-AR", {
        timeZone: TZ,
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const fechaHora = (iso) =>
  iso
    ? new Date(iso).toLocaleString("es-AR", {
        timeZone: TZ,
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const quien = (r) =>
  String(r.nombre ?? "").trim() ||
  (r.patente ? `patente ${r.patente}` : "sin dato");

/* ¿Está esa autorización (por id, venga como texto o número) en estas filas? */
const estaEn = (filas, id) => (filas || []).some((a) => String(a.id) === String(id));

const DIAS = [
  { id: "hoy", label: "Hoy", dias: 1 },
  { id: "semana", label: "7 días", dias: 7 },
];

function rango(dias) {
  const ahora = new Date();
  const hasta = new Date(ahora.getTime() + 24 * 3600 * 1000);
  const desde = new Date(ahora.getTime() - (dias - 1) * 24 * 3600 * 1000);
  desde.setUTCHours(3, 0, 0, 0); // el corte del día vive en UTC−3
  return [desde.toISOString(), hasta.toISOString()];
}

const Vigilancia = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);
  // TRES permisos, no dos: ver, CARGAR (registrar entradas/salidas y los autos
  // del personal — lo de la barrera) y AUTORIZAR (las autorizaciones de gente
  // de afuera — lo hacen los de adentro). La barrera no autoriza a un externo.
  const puedeVer =
    puede("ver.vigilancia") || puede("cargar.vigilancia") || puede("autorizar.vigilancia");
  const puedeCargar = puede("cargar.vigilancia");
  const puedeAutorizar = puede("autorizar.vigilancia");

  const [adentro, setAdentro] = useState(null);
  const [lista, setLista] = useState(null);
  // Las que ya no valen (vencidas o dadas de baja): van en la misma lista,
  // al final y apagadas (ticket 441), y no entran en lo que usa el portón.
  const [noVig, setNoVig] = useState(null);
  // El filtro y la búsqueda de la lista de autorizados viven acá: el buscador
  // (`?ver=`) los vacía para que lo pedido se vea.
  const [vista, setVista] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [movs, setMovs] = useState(null);
  // Las entradas de los últimos días: para ofrecer al escribir los autos que
  // ya entraron y no están en la lista.
  const [recientesReg, setRecientesReg] = useState([]);
  const [cuanto, setCuanto] = useState("hoy");
  const [error, setError] = useState(null);
  const [filtroAdentro, setFiltroAdentro] = useState("");
  // Las salidas que se están guardando (por id del registro) y la última que
  // quedó anotada, para decirlo.
  const [saliendo, setSaliendo] = useState(() => new Set());
  const saliendoRef = useRef(new Set());
  const [avisoSalida, setAvisoSalida] = useState("");
  const refPatente = useRef(null);

  const traer = useCallback(async () => {
    setError(null);
    try {
      const d = DIAS.find((x) => x.id === cuanto) || DIAS[0];
      const [desde, hasta] = rango(d.dias);
      const [a, l, m, nv, rc] = await Promise.all([
        traerAdentro(),
        traerAutorizados(),
        traerMovimientos(desde, hasta),
        // Las no vigentes son para consultar: si no vienen, el portón sigue
        // andando con lo de arriba.
        traerNoVigentes().catch(() => []),
        // Lo mismo las entradas recientes: son una ayuda para escribir.
        traerRecientes().catch(() => []),
      ]);
      setAdentro(a);
      setLista(l);
      setMovs(m);
      setNoVig(nv);
      setRecientesReg(rc || []);
    } catch (e) {
      setError(e?.message || "No se pudo abrir Vigilancia.");
    }
  }, [cuanto]);

  useEffect(() => {
    if (puedeVer) traer();
  }, [traer, puedeVer]);

  /* REGISTRAR SALIDA, con aviso mientras guarda.
   *
   * Antes el botón no cambiaba hasta que volvía a leer todo (uno o dos
   * segundos con 4G flojo): se volvía a tocar en el mismo lugar y, si la lista
   * ya se había redibujado, ese lugar era el renglón de otro, que seguía
   * adentro. Ahora, al tocar, ese botón queda en «Registrando…» y no se puede
   * volver a tocar; si queda anotada, el renglón sale de la lista en ese
   * momento (sin esperar a releer) y abajo se dice a quién. Si no, el renglón
   * sigue donde estaba y se avisa que no quedó anotada.
   *
   * Devuelve si quedó anotada: el aviso «Ya figura adentro» del formulario se
   * vacía sólo en ese caso. */
  const marcarSaliendo = (id, si) => {
    const nuevo = new Set(saliendoRef.current);
    if (si) nuevo.add(id);
    else nuevo.delete(id);
    saliendoRef.current = nuevo;
    setSaliendo(nuevo);
  };
  const salida = async (id) => {
    if (saliendoRef.current.has(id)) return false;
    const r = (adentro || []).find((x) => x.id === id);
    marcarSaliendo(id, true);
    try {
      await registrarSalida(id);
    } catch (e) {
      marcarSaliendo(id, false);
      alert(
        avisoDeError(e, "No se pudo registrar la salida", "Sigue figurando adentro: probá de nuevo."),
      );
      return false;
    }
    setAdentro((a) => (a || []).filter((x) => x.id !== id));
    marcarSaliendo(id, false);
    setAvisoSalida(
      r
        ? `Salida registrada: ${quien(r)}${r.patente && String(r.nombre ?? "").trim() ? ` (${r.patente})` : ""} a las ${horaAr(new Date().toISOString())}.`
        : "Salida registrada.",
    );
    traer();
    return true;
  };

  /* Ir al formulario desde arriba: con 17 autos adentro, «Registrar entrada»
   * queda varias pantallas más abajo en el celular. */
  const irAEntrada = () => {
    const campo = refPatente.current;
    if (!campo) return;
    campo.scrollIntoView?.({ behavior: "smooth", block: "center" });
    campo.focus?.({ preventScroll: true });
  };

  // Los autos que ya entraron y NO están en la lista: los ofrece el formulario.
  const recientes = useMemo(
    () => recientesSinAutorizar(recientesReg, lista || []),
    [recientesReg, lista],
  );
  const adentroVisibles = useMemo(
    () => filtrarPorPatenteONombre(adentro || [], filtroAdentro),
    [adentro, filtroAdentro],
  );

  const movsConSalida = useMemo(
    () => (movs || []).filter((m) => m.salida),
    [movs],
  );

  /* LA AUTORIZACIÓN QUE PIDIÓ EL BUSCADOR. Igna, 02/10: «que se abra en el
   * ítem exacto, no solo la pantalla». El buscador encuentra también las dadas
   * de baja y las vencidas.
   *
   * Desde el ticket 441 todas van en la misma lista: si está, su fila titila
   * (y si había un filtro o una búsqueda puesta, primero se vacían). Si no
   * está (una vencida o dada de baja más vieja que el tope), se pide sola por
   * id y se suma a la lista con su estado a la vista. Si la base no la
   * devuelve, se dice. */
  const { ver, resaltar, listo } = useItemPedido();
  const [fuera, setFuera] = useState(null); // la pedida que las listas no traen
  const [avisoPedido, setAvisoPedido] = useState("");
  const buscado = useRef("");

  const fueraVigente = fuera && estadoAutorizacion(fuera) === "vigente";

  const filasLista = useMemo(
    () =>
      !lista || !fuera || !fueraVigente || estaEn(lista, fuera.id)
        ? lista
        : [...lista, fuera],
    [lista, fuera, fueraVigente],
  );
  const filasNoVig = useMemo(
    () =>
      !noVig || !fuera || fueraVigente || estaEn(noVig, fuera.id)
        ? noVig
        : [...noVig, fuera],
    [noVig, fuera, fueraVigente],
  );
  // UNA lista con todos (ticket 441): las vigentes y las que ya no valen.
  const todas = useMemo(
    () => [...(filasLista || []), ...(filasNoVig || [])],
    [filasLista, filasNoVig],
  );
  // Para «Agregar»: los autos que ya entraron y no están en NINGÚN renglón.
  const recientesFuera = useMemo(
    () => recientesSinAutorizar(recientesReg, todas),
    [recientesReg, todas],
  );

  useEffect(() => {
    if (!ver) buscado.current = "";
    if (!ver || !filasLista || !filasNoVig) return;
    if (estaEn(todas, ver)) {
      // Con un filtro o una búsqueda puesta, la fila puede no estar dibujada:
      // primero se vacían, y al dibujarse vuelve a pasar por acá y la encuentra.
      if (vista !== "todos" || busqueda) {
        setVista("todos");
        setBusqueda("");
        return;
      }
      setAvisoPedido("");
      resaltar(buscarFila(ver));
      listo();
      return;
    }
    if (buscado.current === ver) return; // ya se pidió; espera la respuesta
    buscado.current = ver;
    const noEsta = () => {
      setAvisoPedido("No encontré esa autorización.");
      listo();
    };
    traerAutorizacion(ver)
      .then((a) => (a ? setFuera(a) : noEsta()))
      .catch(noEsta);
  }, [ver, filasLista, filasNoVig, todas, vista, busqueda, resaltar, listo]);

  if (permisos !== null && !puedeVer) {
    return <PuertaCerrada />;
  }
  if (error && !adentro) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!adentro || !lista || !movs || !noVig) return <Loading />;

  return (
    <div className="docs">
      {/* ADENTRO AHORA — la pregunta caliente, arriba de todo. */}
      <div className="vig-novig-cab">
        <h3 className="docs-sub">Adentro ahora · {adentro.length}</h3>
        {puedeCargar && adentro.length > 3 && (
          <button type="button" className="tk-chip" onClick={irAEntrada}>
            Nueva entrada
          </button>
        )}
      </div>
      {adentro.length === 0 ? (
        <p className="panel-aviso suave">No hay nadie registrado adentro.</p>
      ) : (
        <>
          {/* Con muchos adentro, a la salida se lo busca por patente o nombre
              en vez de recorrer la lista. */}
          {adentro.length > 5 && (
            <input
              className="vig-filtro"
              type="search"
              value={filtroAdentro}
              onChange={(e) => setFiltroAdentro(e.target.value)}
              placeholder="Buscar por patente o nombre"
              aria-label="Buscar entre los que están adentro"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          )}
          {adentroVisibles.length === 0 ? (
            <p className="panel-aviso suave">Nadie de adentro coincide con «{filtroAdentro}».</p>
          ) : (
            <div className="tabla-wrap">
              {/* `vig-cards`: en el celular cada renglón es una tarjeta con el
                  botón de la salida a la vista, sin correr la tabla de costado
                  (Guillermo, 13/10: anotar la salida más rápido). */}
              <table className="tabla fija vig-tabla vig-cards vig-adentro">
                <thead>
                  <tr>
                    <th>Entró</th>
                    <th>Quién</th>
                    <th>Patente</th>
                    <th>A quién / motivo</th>
                    <th>Cómo</th>
                    {puedeCargar && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {adentroVisibles.map((r) => (
                    <tr key={r.id}>
                      <td className="tenue vig-c-hora">
                        {hora(r.entrada)}
                        <span className="sub">{haceCuanto(r.entrada)}</span>
                      </td>
                      <td className="vig-c-quien">
                        <b>{quien(r)}</b>
                        {/* Venía en la lista: se ligó al registrar. */}
                        {r.autorizacion_id && (
                          <span className="pill ok">venía autorizado</span>
                        )}
                        {r.dni && <span className="sub">DNI {r.dni}</span>}
                      </td>
                      {/* La patente como chapa Mercosur, igual que la de la flota
                          (Igna, 03/10: «quedan re facheras»). Se guarda
                          normalizada (`patenteNorm`); una que no es argentina
                          —una chilena, una brasileña— sale tal cual y no partida
                          a ojo. */}
                      <td className="vig-c-patente">
                        {r.patente ? <Patente dominio={r.patente} /> : <span className="sin-dato">—</span>}
                      </td>
                      <td className="vig-c-detalle">{r.a_quien || <span className="sin-dato">—</span>}</td>
                      <td className="vig-c-como">{r.como || <span className="sin-dato">—</span>}</td>
                      {puedeCargar && (
                        <td className="vig-c-accion">
                          <button
                            type="button"
                            className="btn btn--secondary vig-sm"
                            disabled={saliendo.has(r.id)}
                            onClick={() => salida(r.id)}
                          >
                            {saliendo.has(r.id) ? "Registrando…" : "Registrar salida"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {/* Debajo de la lista y no arriba: un renglón nuevo arriba correría las
          tarjetas justo cuando se está por tocar la siguiente. */}
      {avisoSalida && (
        <p className="panel-aviso suave" role="status">
          {avisoSalida}
        </p>
      )}

      {/* REGISTRAR ENTRADA */}
      {puedeCargar && (
        <FormEntrada
          ref={refPatente}
          autorizados={lista}
          recientes={recientes}
          adentro={adentro}
          noVigentes={noVig}
          puedeSumar={puedeCargar || puedeAutorizar}
          onRegistrado={traer}
          onSalida={salida}
          onSumado={traer}
        />
      )}

      {/* AUTORIZADOS — una sola lista y un solo «Agregar» (Nico, ticket 441). */}
      {avisoPedido && (
        <p className="panel-aviso suave" role="status">
          {avisoPedido}
        </p>
      )}
      <Autorizados
        filas={todas}
        recientes={recientesFuera}
        puedeCargar={puedeCargar}
        puedeAutorizar={puedeAutorizar}
        onCambio={traer}
        vista={vista}
        onVista={setVista}
        busqueda={busqueda}
        onBusqueda={setBusqueda}
        truncadas={noVig.length >= TOPE_NO_VIGENTES}
        tope={TOPE_NO_VIGENTES}
      />

      {/* MOVIMIENTOS (los que ya entraron y salieron) */}
      <div className="tk-filtros" style={{ marginTop: 18 }}>
        {DIAS.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`tk-chip${cuanto === d.id ? " activo" : ""}`}
            onClick={() => setCuanto(d.id)}
          >
            {d.label}
          </button>
        ))}
      </div>
      <h3 className="docs-sub">Movimientos · {movsConSalida.length}</h3>
      {movsConSalida.length === 0 ? (
        <p className="panel-aviso suave">
          Todavía no hay entradas cerradas en este período.
        </p>
      ) : (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Entró</th>
                <th>Salió</th>
                <th>Quién</th>
                <th>Patente</th>
                <th>A quién / motivo</th>
                <th>Anotó</th>
              </tr>
            </thead>
            <tbody>
              {movsConSalida.map((r) => (
                <tr key={r.id}>
                  <td className="tenue">{fechaHora(r.entrada)}</td>
                  <td className="tenue">{hora(r.salida)}</td>
                  <td>
                    <b>{quien(r)}</b>
                    {r.dni && <span className="sub">DNI {r.dni}</span>}
                  </td>
                  <td>{r.patente ? <Patente dominio={r.patente} /> : <span className="sin-dato">—</span>}</td>
                  <td>{r.a_quien || <span className="sin-dato">—</span>}</td>
                  <td className="tenue">{r.registrado_por || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default Vigilancia;
